import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../services/apiServices';
 
export default function Pos() {
  const { shopCode } = useParams();

  const [business, setBusiness] = useState(null);
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [salesHistory, setSalesHistory] = useState([]);

  // Dynamically load shop-specific menu and business configuration
  useEffect(() => {
    async function loadShopData() {
      if (!shopCode) return;

      try {
        setLoading(true);
        setError(null);

        // Load files dynamically for the shop
        // const businessModule = await import(`../data/shops/${shopCode}/business.json`);
        // const menuModule = await import(`../data/shops/${shopCode}/menu.json`);
          const data = await  apiService.get(`/getlink-apikey/shop-data-json/${shopCode}`);
                const parsedDate = JSON.parse(data.report[0].full_shop_data);
              //  console.log('data:',parsedDate)
                // if (!resBiz.ok || !resMenu.ok) throw new Error('Shop not found');
                const businessModule = parsedDate.business;
                const menuModule = parsedDate.menu;
                
        const loadedBusiness = businessModule.default || businessModule;
        const loadedMenu = menuModule.default || menuModule;

        setBusiness(loadedBusiness);
        setMenuData(loadedMenu);

        // Set initial default payment method
        const defaultPayment = loadedBusiness.paymentMethods?.[0]?.name || 'Cash';
        setPaymentMethod(defaultPayment);

        // Load isolated sales history for this specific shop
        const storageKey = `${shopCode}_pos_orders`;
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          setSalesHistory(JSON.parse(stored));
        } else {
          setSalesHistory([]);
        }
      } catch (err) {
        console.error(`Failed to load POS data for shop '${shopCode}':`, err);
        setError('Failed to load shop configuration.');
      } finally {
        setLoading(false);
      }
    }

    loadShopData();
  }, [shopCode]);

  const addToCart = (item, size = null) => {
    const cartId = size ? `${item.id}-${size.name}` : item.id;
    const name = size ? `${item.name} (${size.name})` : item.name;
    const price = size ? size.price : item.price;

    setCart(prev => {
      const existing = prev.find(i => i.cartId === cartId);
      if (existing) return prev.map(i => i.cartId === cartId ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { cartId, name, price, qty: 1 }];
    });
  };

  const total = cart.reduce((sum, item) => sum + item.price * item.qty, 0);

  const checkoutAndPrint = () => {
    if (cart.length === 0) return alert('Cart is empty');

    const order = {
      id: 'ORD-' + Date.now().toString().slice(-4),
      timestamp: new Date().toLocaleString(),
      items: cart,
      total,
      paymentMethod
    };

    const updated = [order, ...salesHistory];
    setSalesHistory(updated);

    // Save sales history isolated per shop
    const storageKey = `${shopCode}_pos_orders`;
    localStorage.setItem(storageKey, JSON.stringify(updated));

    // Print Receipt
    window.print();
    setCart([]);
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading POS terminal...</div>;
  if (error || !business) return <div style={{ padding: '2rem', textAlign: 'center' }}>{error || 'Shop not found'}</div>;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }} className="no-print">
        <div>
          <h2>{business.name} - POS Terminal</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
            {menuData.map(item => (
              <div key={item.id} style={{ border: '1px solid #CCC', padding: '1rem', borderRadius: '6px' }}>
                <strong>{item.name}</strong>
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                  {item.hasSizes ? item.sizes.map(s => (
                    <button key={s.name} onClick={() => addToCart(item, s)} style={posBtn}>
                      {s.name} ({s.price})
                    </button>
                  )) : (
                    <button onClick={() => addToCart(item)} style={posBtn}>
                      Add ({item.price})
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ border: '1px solid #CCC', padding: '1rem', borderRadius: '6px' }}>
          <h2>Current Order</h2>
          {cart.map(i => (
            <div key={i.cartId} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>{i.qty}x {i.name}</span>
              <span>{business.currency} {i.price * i.qty}</span>
            </div>
          ))}
          <hr />
          <h3>Total: {business.currency} {total}</h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 'bold' }}>
              Payment Method:
            </label>
            <select 
              value={paymentMethod} 
              onChange={e => setPaymentMethod(e.target.value)} 
              style={{ padding: '0.5rem', width: '100%', borderRadius: '4px', border: '1px solid #CCC' }}
            >
              {business.paymentMethods?.map(method => (
                <option key={method.id || method.name} value={method.name}>
                  {method.name}
                </option>
              )) || (
                <>
                  <option value="Cash">Cash</option>
                  <option value="M-PESA">M-PESA</option>
                </>
              )}
            </select>
          </div>

          <button onClick={checkoutAndPrint} style={{ ...posBtn, background: '#3B82F6', width: '100%', padding: '0.75rem' }}>
            Complete & Print Receipt
          </button>
        </div>
      </div>

      {/* Printable Receipt Layout */}
      <div id="receipt-print" className="print-only">
        <h3 style={{ textAlign: 'center', margin: 0 }}>{business.name}</h3>
        <p style={{ textAlign: 'center', margin: 0 }}>{business.address}</p>
        <p style={{ textAlign: 'center', margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>{business.phone}</p>
        <hr />
        {cart.map(i => (
          <div key={i.cartId} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{i.qty}x {i.name}</span>
            <span>{business.currency} {i.price * i.qty}</span>
          </div>
        ))}
        <hr />
        <p><strong>Total: {business.currency} {total}</strong></p>
        <p>Payment: {paymentMethod}</p>
        <p style={{ textAlign: 'center' }}>Thank you for visiting!</p>
      </div>
    </div>
  );
}

const posBtn = { padding: '0.4rem 0.8rem', background: '#111827', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' };